import React, { useEffect, useState, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import ReviewQueueToolbar from "../../../../components/dashboardcomponents/KnowledgeBase/ReviewQueue/ReviewQueueToolbar";
import ReviewQueueTable from "../../../../components/dashboardcomponents/KnowledgeBase/ReviewQueue/ReviewQueueTable";
import ReviewQueueSkeleton from "../../../../components/dashboardcomponents/KnowledgeBase/ReviewQueue/ReviewQueueSkeleton";
import { getKbReviewArticles, getKbReviewTabCounts } from "../../../../store/features/getKbReviewArticles";
import GroupSelectDropdown from "../../../../components/dashboardcomponents/ProfileComponents/GroupSelectDropdown";
import { useAnalyticsGroupId } from "../../../../hooks/useAnalyticsGroupId";
import useDebounce from "../../../../hooks/useDebounce";
import { isHeaderInlinePageTitleEnabled } from "../../../../config/featureFlags";
import "../../TicketManagment/style.css";

const TABS = [
  { key: "all", label: "All" },
  { key: "pendingReview", label: "Pending Review" },
  { key: "draft", label: "Drafts" },
];

const ReviewQueue = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightArticleId = searchParams.get("articleId");
  const groupIdParam = searchParams.get("group_id");
  const business_id = useSelector((state) => state.loginToken?.response?.user?.business_id);
  const { articles, totalCount, loading, error, tabCounts, tabCountsGroupId } = useSelector(
    (state) => state.kbReviewArticles
  );

  const [tab, setTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [forceSkeleton, setForceSkeleton] = useState(false);
  const { selectedGroupId, setSelectedGroupId, syncGroupId } = useAnalyticsGroupId();
  const rowsPerPage = 10;
  const debouncedSearch = useDebounce(searchQuery, 400);
  const isLoading = !selectedGroupId || loading || forceSkeleton;

  useEffect(() => {
    if (!groupIdParam) return;
    if (String(groupIdParam) === String(selectedGroupId)) return;
    syncGroupId(groupIdParam);
    setPage(0);
  }, [groupIdParam, selectedGroupId, syncGroupId]);

  const loadArticles = useCallback((p, t, s) => {
    if (business_id && selectedGroupId) {
      dispatch(getKbReviewArticles({
        business_id,
        group_id: selectedGroupId,
        page: p + 1,
        limit: rowsPerPage,
        status: t,
        search: s
      }));
    }
  }, [dispatch, business_id, selectedGroupId]);

  useEffect(() => {
    loadArticles(page, tab, debouncedSearch);
  }, [page, tab, debouncedSearch, loadArticles]);

  useEffect(() => {
    if (business_id && selectedGroupId) {
      dispatch(getKbReviewTabCounts({ business_id, group_id: selectedGroupId, search: debouncedSearch }));
    }
  }, [dispatch, business_id, selectedGroupId, debouncedSearch]);

  // Tab switches use soft loading (loading can stay false while stale rows show), so `loading`
  // alone may not change — still clear the tab-change skeleton when the new payload lands.
  useEffect(() => {
    if (!loading) {
      setForceSkeleton(false);
    }
  }, [loading, articles, totalCount, error]);

  useEffect(() => {
    if (!highlightArticleId) return;
    setTab("pendingReview");
    setPage(0);
    setForceSkeleton(true);
  }, [highlightArticleId]);

  const clearArticleIdParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("articleId");
        next.delete("group_id");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setPage(0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const rows = useMemo(() => (articles || []).map(a => ({
    id: a._id,
    title: a.title,
    status: a.status,
    contributor: a.authorId?.full_name || 'Unknown',
    dateSubmitted: new Date(a.reviewQueue?.submittedAt || a.updatedAt || a.createdAt).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    })
  })), [articles]);

  useEffect(() => {
    if (!highlightArticleId || loading) return;
    const id = String(highlightArticleId);
    const match = rows.some((r) => String(r.id) === id);
    if (!match && !forceSkeleton) {
      const t = window.setTimeout(() => clearArticleIdParam(), 2000);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [
    highlightArticleId,
    loading,
    rows,
    forceSkeleton,
    clearArticleIdParam,
  ]);

  const getTabCount = (key) => {
    const counts =
      selectedGroupId &&
      tabCountsGroupId != null &&
      String(tabCountsGroupId) === String(selectedGroupId)
        ? tabCounts
        : { all: 0, pendingReview: 0, draft: 0 };
    return counts[key] || 0;
  };

  return (
    <Box sx={{ paddingLeft: '24px', paddingRight: '24px', paddingBottom: '8px', backgroundColor: '#fff', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div>
        {/* Legacy in-page title + group — hidden when header shows them (Application V2) */}
        {!isHeaderInlinePageTitleEnabled && (
          <div className="profile-header-row">
            <p className="Ticket-text">Review Queue</p>
            <GroupSelectDropdown
              onSelect={setSelectedGroupId}
              value={selectedGroupId}
            />
          </div>
        )}
        <div
          className="Ticket-main-header"
          style={{ marginTop: isHeaderInlinePageTitleEnabled ? 0 : '24px' }}
        >
          <div className="Ticket-text-section">
            {TABS.map((t) => (
              <p
                key={t.key}
                className={`ticket-header-text ${tab === t.key ? "TicketHeaderActive" : ""}`}
                onClick={() => handleTabChange(t.key)}
                style={{ cursor: 'pointer', fontWeight: tab === t.key ? 600 : 400 }}
              >
                {t.label} ({getTabCount(t.key)})
              </p>
            ))}
          </div>
        </div>
      </div>

      <ReviewQueueToolbar
        searchQuery={searchQuery}
        setSearchQuery={(val) => { setSearchQuery(val); setPage(0); }}
      />

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {isLoading ? (
          <ReviewQueueSkeleton />
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', marginTop: '24px' }}>

            <ReviewQueueTable
              rows={rows}
              page={page}
              rowsPerPage={rowsPerPage}
              onChangePage={(p) => handlePageChange(p)}
              totalCount={totalCount}
              highlightArticleId={highlightArticleId}
              onHighlightConsumed={clearArticleIdParam}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ReviewQueue;